import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import SanctumService from "@/core/services/sanctum.service";
import { authGet, clearAuthStorage } from "@/core/services/auth-storage";

/** Non-blocking top progress bar: body.page-loading (see _page-loader.scss) */
let pageLoadingDepth = 0;

function normalizeRequestPath(config) {
  const raw = (config && config.url ? String(config.url) : "").split("?")[0];
  return raw.replace(/^\/+/, "");
}

function shouldUseFullPageLoading(config) {
  if (!config) return false;
  if (config.skipPageLoading === true) return false;
  const headers = config.headers || {};
  if (headers["X-Skip-Page-Loading"] === "1" || headers.common?.["X-Skip-Page-Loading"] === "1") {
    return false;
  }
  const method = String(config.method || "get").toLowerCase();
  // List/poll GETs already have in-page skeletons — don't blank the whole shell
  if (method === "get" || method === "head") {
    return false;
  }
  const path = normalizeRequestPath(config);
  const skipPaths = [
    "business/sales/orders",
    "business/sales/sidebar-counts",
    "email-prices",
    "email-price",
    "email-wallet",
    "pay-for-emails",
    "get-address",
    "business/settings/payment-methods",
    "business/static/food-types",
    "sanctum/csrf-cookie",
    "user",
    "migrate-domain",
    "fcm",
  ];
  return !skipPaths.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));
}

function releasePageLoading(config) {
  if (!config || !config.__pageLoadingTracked) return;
  config.__pageLoadingTracked = false;
  pageLoadingDepth = Math.max(0, pageLoadingDepth - 1);
  if (pageLoadingDepth === 0) {
    document.body.classList.remove("page-loading");
  }
}

axios.interceptors.request.use(
  function (config) {
    if (shouldUseFullPageLoading(config)) {
      config.__pageLoadingTracked = true;
      pageLoadingDepth += 1;
      if (pageLoadingDepth === 1) {
        document.body.classList.add("page-loading");
      }
    } else {
      config.__pageLoadingTracked = false;
    }

    const token = authGet('ID_TOKEN');
    const isStaff = authGet('is_staff');
    const staffId = authGet('staff_id');

    // Set Authorization token
    config.headers.common.Authorization = `Bearer ${token}`;

    // If the user is staff, send additional headers
    if (isStaff == '1') {
      config.headers.common['X-Is-Staff'] = '1';
      config.headers.common['X-Staff-Id'] = staffId;
    }

    // If sending FormData, remove Content-Type header to let browser/axios set it automatically
    // This prevents CORS issues that occur when manually setting Content-Type for multipart/form-data
    if (config.data instanceof FormData) {
      delete config.headers.common['Content-Type'];
      delete config.headers.post['Content-Type'];
      delete config.headers['Content-Type'];
    }

    return config;
  },

  function (error) {
    if (error && error.config) {
      releasePageLoading(error.config);
    } else {
      document.body.classList.remove("page-loading");
      pageLoadingDepth = 0;
    }
    return Promise.reject(error);
  }
);
axios.interceptors.response.use(
  function (response) {
    releasePageLoading(response && response.config);
    return Promise.resolve(response);
  },
  function (error) {
    releasePageLoading(error && error.config);

    const status = error && error.response && error.response.status;
    const onLogin =
      typeof window !== "undefined" &&
      window.location.href.includes("login");

    // Expired / invalid session — clear portal auth and send to login
    if ((status === 401 || status === 419) && !onLogin) {
      clearAuthStorage();
      window.location.hash = "#/login";
    }

    return Promise.reject(error);
  }
);
/**
 * Service to call HTTP request via Axios
 */
const ApiService = {

  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
    Vue.axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
    Vue.axios.defaults.headers.common["Accept"] = "application/json";
    Vue.axios.defaults.headers.common["Content-Type"] = "application/json";
    Vue.axios.defaults.withCredentials = true;
  },

  /**
   * Set the default HTTP request headers
   */
  // setHeader() {
  //   Vue.axios.defaults.headers.common[
  //     "Authorization"
  //   ] = `Bearer `+sessionStorage.getItem('ID_TOKEN');
  // },

  query(resource, params) {
    return Vue.axios.get(resource, params).catch(error => {
      // console.log(error);
      throw new Error(`[KT] ApiService ${error}`);
    });
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  setCookie() {
    return Vue.axios
      .get(process.env.VUE_APP_API_BASE_URL + "sanctum/csrf-cookie")
      .catch(error => {
        throw new Error(`[KT] ApiService ${error}`);
      });
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource, slug = "") {
    let url = resource;
    if (slug) url += "/" + slug;
    const branchId = authGet("SELECTED_BRANCH");
    if (branchId) {
      url += url.includes("?") ? `&branch_id=${branchId}` : `?branch_id=${branchId}`;
    }

    return Vue.axios.get(url).catch(error => {
      // console.log(error);
      throw new Error(`[KT] ApiService ${error}`);
    });
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params) {
    const branchId = authGet("SELECTED_BRANCH");
    if (branchId) {
      resource += resource.includes("?") ? `&branch_id=${branchId}` : `?branch_id=${branchId}`;
    }
    return Vue.axios.post(`${resource}`, params);
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  fileUpload(resource, params) {
    const branchId = authGet("SELECTED_BRANCH");
    if (branchId) {
      resource += resource.includes("?") ? `&branch_id=${branchId}` : `?branch_id=${branchId}`;
    }

    // Don't set Content-Type manually - let axios/browser set it automatically with correct boundary
    // This prevents CORS issues that occur when manually setting Content-Type header
    // When FormData is passed, axios automatically detects it and sets Content-Type to multipart/form-data with boundary
    return Vue.axios.post(
      `${resource}`,
      params,
      {
        transformRequest: [(data, headers) => {
          // Remove Content-Type header to let axios set it automatically for FormData
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }]
      }
    );
  },


  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return Vue.axios.delete(resource).catch(error => {
      // console.log(error);
      throw new Error(`[RWV] ApiService ${error}`);
    });
  }
};

export default ApiService;
